import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, ArrowLeftRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card } from '../ui/card';
import OrganizationInfo from './OrganizationInfo';
import MemberManagement from './MemberManagement';
import OrganizationSwitcher from './OrganizationSwitcher';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { setCurrentOrganization, setMembers, setLoading, setError } from '../../redux/slices/organizationSlice';
import organizationService from '../../services/organization.service';
import { userSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';

/**
 * Organization Settings Page
 * Main page for managing organization settings, members, and switching organizations
 */
const OrganizationSettings: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState('info');

    const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));
    const organization = useAppSelector(state => state.organization.currentOrganization);
    const loading = useAppSelector(state => state.organization.loading);
    const error = useAppSelector(state => state.organization.error);

    useEffect(() => {
        loadOrganizationData();
    }, []);

    const loadOrganizationData = async () => {
        try {
            dispatch(setLoading(true));
            dispatch(setError(null));

            // Fetch organization details
            const org = await organizationService.getCurrentOrganization();
            dispatch(setCurrentOrganization(org));

            // Fetch members
            const members = await organizationService.getMembers();
            dispatch(setMembers(members));
        } catch (err: any) {
            console.error('Failed to load organization data:', err);
            dispatch(setError(err.response?.data?.error || err.message || 'Failed to load organization data'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    if (loading && !organization) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
                    <p className="text-muted-foreground">{t('common.labels.loading')}</p>
                </div>
            </div>
        );
    }

    if (error && !organization) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Card className="p-6 max-w-md">
                    <div className="text-center">
                        <Building2 className="h-12 w-12 text-destructive mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">{t('organization.settings.loadFailed')}</h2>
                        <p className="text-sm text-muted-foreground mb-4">{error}</p>
                        <button
                            onClick={loadOrganizationData}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                        >
                            {t('common.buttons.refresh')}
                        </button>
                    </div>
                </Card>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Card className="p-6 max-w-md">
                    <div className="text-center">
                        <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">{t('organization.settings.noOrganization')}</h2>
                        <p className="text-sm text-muted-foreground">
                            {t('organization.settings.noOrganization')}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="container max-w-5xl mx-auto py-8 px-4">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">{t('organization.settings.title')}</h1>
                <p className="text-muted-foreground">{t('organization.settings.description')}</p>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="info" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('organization.settings.tabs.info')}</span>
                        <span className="sm:hidden">{t('organization.settings.tabs.info')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="members" className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('organization.settings.tabs.members')}</span>
                        <span className="sm:hidden">{t('organization.settings.tabs.members')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="switcher" className="flex items-center gap-2">
                        <ArrowLeftRight className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('organization.settings.tabs.switcher')}</span>
                        <span className="sm:hidden">{t('organization.settings.tabs.switcher')}</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4">
                    <OrganizationInfo
                        organization={organization}
                        currentUser={currentUser}
                        onUpdate={loadOrganizationData}
                    />
                </TabsContent>

                <TabsContent value="members" className="space-y-4">
                    <MemberManagement
                        currentUser={currentUser}
                        onUpdate={loadOrganizationData}
                    />
                </TabsContent>

                <TabsContent value="switcher" className="space-y-4">
                    <OrganizationSwitcher currentUser={currentUser} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default OrganizationSettings;
